// src/pages/CashAndBank/CashAndBank.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot, query, serverTimestamp, orderBy, doc, writeBatch, getDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useLocation } from 'react-router-dom';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    headerLeft: {
        display: 'flex',
        flexDirection: 'column'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    subtitle: {
        fontSize: '16px',
        color: '#64748b',
        marginTop: '4px'
    },
    headerActions: {
        display: 'flex',
        gap: '12px'
    },
    button: {
        padding: '12px 24px',
        border: 'none',
        background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
        color: 'white',
        borderRadius: '12px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
        transition: 'all 0.2s ease-in-out'
    },
    box: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        marginBottom: '24px'
    },
    tableContainer: {
        maxHeight: '60vh',
        overflowY: 'auto',
        border: '1px solid #e2e8f0',
        borderRadius: '12px'
    },
    table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden'
    },
    th: {
        background: '#f8fafc',
        padding: '16px 20px',
        textAlign: 'left',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
        fontSize: '12px',
        color: '#374151',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        position: 'sticky',
        top: 0
    },
    td: {
        padding: '16px 20px',
        borderBottom: '1px solid #f1f5f9',
        fontSize: '14px',
        color: '#374151'
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 100
    },
    modalContent: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '600px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
    },
    modalActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '24px'
    },
    formGroup: { marginBottom: '20px', position: 'relative' },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: '600',
        color: '#374151',
        fontSize: '14px'
    },
    input: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s'
    },
    select: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        background: 'white',
        outline: 'none'
    },
    income: {
        color: '#059669',
        fontWeight: '600'
    },
    expense: {
        color: '#dc2626',
        fontWeight: '600'
    },
    suggestions: {
        position: 'absolute',
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        width: '100%',
        maxHeight: '150px',
        overflowY: 'auto',
        zIndex: 101,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
    },
    suggestionItem: {
        padding: '12px 16px',
        cursor: 'pointer',
        transition: 'background 0.2s',
        borderBottom: '1px solid #f1f5f9'
    },
    summaryCards: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
    },
    summaryCard: {
        background: 'white',
        padding: '24px',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        textAlign: 'center',
        transition: 'all 0.2s ease-in-out'
    },
    cardTitle: {
        fontSize: '14px',
        fontWeight: '500',
        color: '#64748b',
        marginBottom: '8px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    cardValue: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#0f172a'
    },
    emptyState: {
        textAlign: 'center',
        padding: '64px 32px',
        color: '#64748b'
    },
    emptyIcon: {
        fontSize: '48px',
        marginBottom: '16px'
    },
    emptyText: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '8px'
    },
    emptySubtext: {
        fontSize: '14px',
        color: '#64748b'
    }
};

function AddTransactionModal({ type, onClose, onSave, parties, initialData }) {
    const [transaction, setTransaction] = useState({
        amount: '',
        description: '',
        relatedPartyId: initialData?.relatedPartyId || '',
        relatedPartyName: initialData?.relatedPartyName || '',
        currency: 'TRY',
        exchangeRate: 1,
        paymentMethod: 'Nakit',
        dueDate: ''
    });
    const [partyNameInput, setPartyNameInput] = useState(initialData?.relatedPartyName || '');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setTransaction(prev => ({ ...prev, [name]: value }));
    };

    const handlePartySelect = (party) => {
        setPartyNameInput(party.name);
        setTransaction(prev => ({
            ...prev,
            relatedPartyId: party.id,
            relatedPartyName: party.name
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!transaction.relatedPartyId) {
            alert("Lütfen listeden geçerli bir müşteri veya tedarikçi seçin.");
            return;
        }
        onSave({ ...transaction, type });
    };

    const filteredParties = partyNameInput
        ? parties.filter(p => p.name.toLowerCase().includes(partyNameInput.toLowerCase()))
        : [];

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={{ ...styles.title, fontSize: '24px' }}>Yeni {type} Ekle</h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#64748b' }}>&times;</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>{type === 'Tahsilat' ? 'Müşteri' : 'Tedarikçi'}</label>
                        <input
                            type="text"
                            style={styles.input}
                            placeholder="Müşteri / Tedarikçi Ara..."
                            value={partyNameInput}
                            onChange={e => { setPartyNameInput(e.target.value); setTransaction(prev => ({ ...prev, relatedPartyId: '' })) }}
                            required
                        />
                        {filteredParties.length > 0 && partyNameInput && !transaction.relatedPartyId && (
                            <div style={styles.suggestions}>
                                {filteredParties.map(party => (
                                    <div key={party.id} style={styles.suggestionItem} onMouseDown={() => handlePartySelect(party)}>
                                        {party.name}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Tutar</label>
                            <input style={styles.input} type="number" name="amount" value={transaction.amount} onChange={handleChange} required />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Döviz Türü</label>
                            <select
                                name="currency"
                                value={transaction.currency}
                                onChange={handleChange}
                                style={styles.select}
                            >
                                <option value="TRY">TL (₺)</option>
                                <option value="USD">USD ($)</option>
                            </select>
                        </div>
                    </div>
                    {transaction.currency === 'USD' && (
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Döviz Kuru (1 USD = ? TL)</label>
                            <input
                                style={styles.input}
                                type="number"
                                name="exchangeRate"
                                step="0.0001"
                                value={transaction.exchangeRate}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                        <div>
                            <label style={styles.label}>Ödeme Yöntemi</label>
                            <select
                                name="paymentMethod"
                                value={transaction.paymentMethod}
                                onChange={handleChange}
                                style={styles.select}
                            >
                                <option value="Nakit">Nakit</option>
                                <option value="Kredi Kartı">Kredi Kartı</option>
                                <option value="Çek">Çek</option>
                                <option value="Havale/EFT">Havale/EFT</option>
                            </select>
                        </div>
                        <div>
                            <label style={styles.label}>Vade Tarihi (Opsiyonel)</label>
                            <input
                                style={styles.input}
                                type="date"
                                name="dueDate"
                                value={transaction.dueDate}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Açıklama</label>
                        <input style={styles.input} type="text" name="description" value={transaction.description} onChange={handleChange} required />
                    </div>
                    <div style={styles.modalActions}>
                        <button type="button" onClick={onClose} style={{ ...styles.button, background: 'linear-gradient(135deg, #64748b, #475569)', boxShadow: '0 4px 12px rgba(100, 116, 139, 0.3)' }}>İptal</button>
                        <button type="submit" style={{ ...styles.button, background: type === 'Tahsilat' ? 'linear-gradient(135deg, #059669, #047857)' : 'linear-gradient(135deg, #dc2626, #b91c1c)', boxShadow: `0 4px 12px ${type === 'Tahsilat' ? 'rgba(5, 150, 105, 0.3)' : 'rgba(220, 38, 38, 0.3)'}` }}>Kaydet</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function CashAndBank() {
    const { userData, canViewPrices } = useAuth();
    const location = useLocation();
    const [transactions, setTransactions] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(null);
    const [modalInitialData, setModalInitialData] = useState(null);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedTransaction, setEditedTransaction] = useState(null);

    useEffect(() => {
        if (!userData?.companyId) {
            setLoading(false);
            return;
        }
        const companyId = userData.companyId;

        const transQuery = query(collection(db, 'companies', companyId, 'transactions'), orderBy('date', 'desc'));
        const unsubTrans = onSnapshot(transQuery, (snapshot) => {
            setTransactions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });

        const custQuery = query(collection(db, 'companies', companyId, 'customers'));
        const unsubCust = onSnapshot(custQuery, (snapshot) => setCustomers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))));

        const suppQuery = query(collection(db, 'companies', companyId, 'suppliers'));
        const unsubSupp = onSnapshot(suppQuery, (snapshot) => setSuppliers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))));

        return () => { unsubTrans(); unsubCust(); unsubSupp(); };
    }, [userData]);

    // Handle external navigation
    useEffect(() => {
        if (location.state?.action === 'add_collection' && location.state?.customerId) {
            setModalInitialData({
                relatedPartyId: location.state.customerId,
                relatedPartyName: location.state.customerName
            });
            setShowModal('Tahsilat');
        }
    }, [location.state]);


    const handleAddTransaction = async (transactionData) => {
        if (!userData?.companyId) return;

        const companyId = userData.companyId;
        const batch = writeBatch(db);
        const amount = Number(transactionData.amount);

        const transactionRef = doc(collection(db, 'companies', companyId, 'transactions'));
        batch.set(transactionRef, {
            ...transactionData,
            amount: amount,
            date: serverTimestamp()
        });

        const isIncome = transactionData.type === 'Tahsilat';
        const partyCollection = isIncome ? 'customers' : 'suppliers';
        const partyRef = doc(db, 'companies', companyId, partyCollection, transactionData.relatedPartyId);

        try {
            const partyDoc = await getDoc(partyRef);
            if (partyDoc.exists()) {
                const partyData = partyDoc.data();
                const partyCurrency = partyData.currency || 'TRY';
                const transCurrency = transactionData.currency || 'TRY';
                const transExchangeRate = Number(transactionData.exchangeRate) || 1;

                let balanceImpact = amount;
                if (transCurrency === 'USD' && partyCurrency === 'TRY') {
                    balanceImpact = amount * transExchangeRate;
                } else if (transCurrency === 'TRY' && partyCurrency === 'USD') {
                    balanceImpact = amount / transExchangeRate;
                }

                const currentBalance = partyData.balance || 0;
                const newBalance = isIncome ? currentBalance + balanceImpact : currentBalance - balanceImpact;
                batch.update(partyRef, { balance: newBalance });
            }

            await batch.commit();
            setShowModal(null);
            setModalInitialData(null); // Clear initial data after save
        } catch (error) {
            console.error("İşlem kaydedilirken hata:", error);
            alert("İşlem sırasında bir hata oluştu.");
        }
    };

    const handleDeleteTransaction = async (transaction) => {
        if (!window.confirm("Bu işlemi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.")) {
            return;
        }

        const companyId = userData.companyId;
        const batch = writeBatch(db);

        // Delete transaction
        const transRef = doc(db, 'companies', companyId, 'transactions', transaction.id);
        batch.delete(transRef);

        // Revert party balance
        const isIncome = transaction.type === 'Tahsilat';
        const partyCollection = isIncome ? 'customers' : 'suppliers';
        const partyRef = doc(db, 'companies', companyId, partyCollection, transaction.relatedPartyId);

        try {
            const partyDoc = await getDoc(partyRef);
            if (partyDoc.exists()) {
                const partyData = partyDoc.data();
                const partyCurrency = partyData.currency || 'TRY';
                const transCurrency = transaction.currency || 'TRY';
                const transExchangeRate = Number(transaction.exchangeRate) || 1;

                let balanceRevertImpact = transaction.amount;
                if (transCurrency === 'USD' && partyCurrency === 'TRY') {
                    balanceRevertImpact = transaction.amount * transExchangeRate;
                } else if (transCurrency === 'TRY' && partyCurrency === 'USD') {
                    balanceRevertImpact = transaction.amount / transExchangeRate;
                }

                const currentBalance = partyData.balance || 0;
                // Revert: if income was added, subtract; if expense was subtracted, add back
                const newBalance = isIncome ? currentBalance - balanceRevertImpact : currentBalance + balanceRevertImpact;
                batch.update(partyRef, { balance: newBalance });
            }

            await batch.commit();
            setSelectedTransaction(null);
            alert('İşlem başarıyla silindi.');
        } catch (error) {
            console.error("İşlem silinirken hata:", error);
            alert("İşlem silinirken bir hata oluştu.");
        }
    };

    const handleUpdateTransaction = async () => {
        if (!editedTransaction) return;

        const companyId = userData.companyId;
        const batch = writeBatch(db);
        const originalTransaction = selectedTransaction;
        const amountDifference = Number(editedTransaction.amount) - originalTransaction.amount;

        // Update transaction
        const transRef = doc(db, 'companies', companyId, 'transactions', originalTransaction.id);
        batch.update(transRef, {
            amount: Number(editedTransaction.amount),
            description: editedTransaction.description,
            paymentMethod: editedTransaction.paymentMethod || 'Nakit',
            dueDate: editedTransaction.dueDate || ''
        });

        // Update party balance if amount changed
        if (amountDifference !== 0) {
            const isIncome = originalTransaction.type === 'Tahsilat';
            const partyCollection = isIncome ? 'customers' : 'suppliers';
            const partyRef = doc(db, 'companies', companyId, partyCollection, originalTransaction.relatedPartyId);

            try {
                const partyDoc = await getDoc(partyRef);
                if (partyDoc.exists()) {
                    const partyData = partyDoc.data();
                    const partyCurrency = partyData.currency || 'TRY';
                    const transCurrency = originalTransaction.currency || 'TRY';
                    const transExchangeRate = Number(originalTransaction.exchangeRate) || 1;

                    let balanceDiffImpact = amountDifference;
                    if (transCurrency === 'USD' && partyCurrency === 'TRY') {
                        balanceDiffImpact = amountDifference * transExchangeRate;
                    } else if (transCurrency === 'TRY' && partyCurrency === 'USD') {
                        balanceDiffImpact = amountDifference / transExchangeRate;
                    }

                    const currentBalance = partyData.balance || 0;
                    const newBalance = isIncome ? currentBalance + balanceDiffImpact : currentBalance - balanceDiffImpact;
                    batch.update(partyRef, { balance: newBalance });
                }
            } catch (error) {
                console.error("Bakiye güncellenirken hata:", error);
            }
        }

        try {
            await batch.commit();
            setIsEditing(false);
            setSelectedTransaction(null);
            setEditedTransaction(null);
            alert('İşlem başarıyla güncellendi.');
        } catch (error) {
            console.error("İşlem güncellenirken hata:", error);
            alert("İşlem güncellenirken bir hata oluştu.");
        }
    };

    const handleTransactionClick = (transaction) => {
        setSelectedTransaction(transaction);
        setEditedTransaction({ ...transaction });
        setIsEditing(false);
    };

    // Pass modalInitialData to AddTransactionModal
    // Need to make sure the JSX uses it

    /* Rest of the component logic needs to be updated too in the full file, but here I'm replacing the top half. 
       I need to make sure the render part uses 'modalInitialData'.
    */

    const totalIncome = useMemo(() => transactions.filter(t => t.type === 'Tahsilat').reduce((sum, t) => sum + t.amount, 0), [transactions]);
    const totalExpense = useMemo(() => transactions.filter(t => t.type === 'Ödeme').reduce((sum, t) => sum + t.amount, 0), [transactions]);
    const totalBalance = useMemo(() => totalIncome - totalExpense, [totalIncome, totalExpense]);

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <h1 style={styles.title}>Kasa & Banka</h1>
                    <p style={styles.subtitle}>Nakit akışınızı yönetin ve takip edin</p>
                </div>
                <div style={styles.headerActions}>
                    <button onClick={() => setShowModal('Ödeme')} style={{ ...styles.button, background: 'linear-gradient(135deg, #dc2626, #b91c1c)', boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)' }}>
                        + Yeni Ödeme
                    </button>
                    <button onClick={() => setShowModal('Tahsilat')} style={{ ...styles.button, background: 'linear-gradient(135deg, #059669, #047857)', boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)' }}>
                        + Yeni Tahsilat
                    </button>
                </div>
            </div>

            {/* Özet Kartlar */}
            <div style={styles.summaryCards}>
                <div style={styles.summaryCard}>
                    <div style={styles.cardTitle}>Toplam Gelir</div>
                    <div style={{ ...styles.cardValue, color: '#059669' }}>{canViewPrices ? totalIncome.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) : '***'} ₺</div>
                </div>
                <div style={styles.summaryCard}>
                    <div style={styles.cardTitle}>Toplam Gider</div>
                    <div style={{ ...styles.cardValue, color: '#dc2626' }}>{canViewPrices ? totalExpense.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) : '***'} ₺</div>
                </div>
                <div style={styles.summaryCard}>
                    <div style={styles.cardTitle}>Net Bakiye</div>
                    <div style={{ ...styles.cardValue, color: totalBalance >= 0 ? '#059669' : '#dc2626' }}>
                        {canViewPrices ? totalBalance.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) : '***'} ₺
                    </div>
                </div>
            </div>

            <div style={styles.box}>
                {loading ? (
                    <div style={styles.emptyState}>
                        <div>Yükleniyor...</div>
                    </div>
                ) : transactions.length === 0 ? (
                    <div style={styles.emptyState}>
                        <div style={styles.emptyIcon}>💰</div>
                        <div style={styles.emptyText}>Henüz finansal hareket bulunmuyor</div>
                        <div style={styles.emptySubtext}>İlk işleminizi eklemek için yukarıdaki butonları kullanın</div>
                    </div>
                ) : (
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Tarih</th>
                                    <th style={styles.th}>Vade</th>
                                    <th style={styles.th}>İşlem Tipi</th>
                                    <th style={styles.th}>Yöntem</th>
                                    <th style={styles.th}>İlgili Taraf</th>
                                    <th style={styles.th}>Açıklama</th>
                                    <th style={styles.th}>Tutar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.map(t => {
                                    let isOverdue = false;
                                    if (t.dueDate) {
                                        const due = new Date(t.dueDate);
                                        const now = new Date();
                                        now.setHours(0, 0, 0, 0);
                                        if (due < now) isOverdue = true;
                                    }
                                    return (
                                        <tr key={t.id} onClick={() => handleTransactionClick(t)} style={{ cursor: 'pointer', transition: 'background 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                                            <td style={styles.td}>{t.date ? new Date(t.date.seconds * 1000).toLocaleDateString('tr-TR') : '-'}</td>
                                            <td style={{ ...styles.td, color: isOverdue ? '#dc2626' : '#374151', fontWeight: isOverdue ? '600' : 'normal' }}>
                                                {t.dueDate ? new Date(t.dueDate).toLocaleDateString('tr-TR') : '-'}
                                            </td>
                                            <td style={{ ...styles.td, ...(t.type === 'Tahsilat' ? styles.income : styles.expense), fontWeight: '600' }}>{t.type}</td>
                                            <td style={styles.td}>
                                                <span style={{ padding: '4px 8px', borderRadius: '4px', background: '#f1f5f9', fontSize: '12px', fontWeight: '500' }}>
                                                    {t.paymentMethod || 'Nakit'}
                                                </span>
                                            </td>
                                            <td style={styles.td}>{t.relatedPartyName}</td>
                                            <td style={styles.td}>{t.description}</td>
                                            <td style={{ ...styles.td, ...(t.type === 'Tahsilat' ? styles.income : styles.expense), fontWeight: '600' }}>
                                                {canViewPrices ? (
                                                    <>
                                                        {(t.type === 'Tahsilat' ? '+' : '-')}{t.amount.toFixed(2)} {t.currency === 'USD' ? '$' : '₺'}
                                                        {t.currency === 'USD' && <div style={{ fontSize: '11px', color: '#666', fontWeight: 'normal' }}>Kur: {t.exchangeRate}</div>}
                                                    </>
                                                ) : '***'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showModal && (
                <AddTransactionModal
                    type={showModal}
                    onClose={() => setShowModal(null)}
                    onSave={handleAddTransaction}
                    parties={showModal === 'Tahsilat' ? customers : suppliers}
                />
            )}

            {/* İşlem Detay Modal */}
            {selectedTransaction && (
                <div style={styles.modalOverlay} onClick={() => { setSelectedTransaction(null); setIsEditing(false); }}>
                    <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h2 style={{ ...styles.title, fontSize: '24px' }}>İşlem Detayı</h2>
                            <button onClick={() => { setSelectedTransaction(null); setIsEditing(false); }} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#64748b' }}>&times;</button>
                        </div>
                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                <div>
                                    <label style={styles.label}>İşlem Tipi</label>
                                    <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', fontWeight: '600', color: selectedTransaction.type === 'Tahsilat' ? '#059669' : '#dc2626' }}>{selectedTransaction.type}</div>
                                </div>
                                <div>
                                    <label style={styles.label}>Tarih</label>
                                    <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>{selectedTransaction.date ? new Date(selectedTransaction.date.seconds * 1000).toLocaleDateString('tr-TR') : '-'}</div>
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                <div>
                                    <label style={styles.label}>Yöntem</label>
                                    {isEditing ? (
                                        <select
                                            style={styles.select}
                                            value={editedTransaction.paymentMethod || 'Nakit'}
                                            onChange={(e) => setEditedTransaction(prev => ({ ...prev, paymentMethod: e.target.value }))}
                                        >
                                            <option value="Nakit">Nakit</option>
                                            <option value="Kredi Kartı">Kredi Kartı</option>
                                            <option value="Çek">Çek</option>
                                            <option value="Havale/EFT">Havale/EFT</option>
                                        </select>
                                    ) : (
                                        <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>{selectedTransaction.paymentMethod || 'Nakit'}</div>
                                    )}
                                </div>
                                <div>
                                    <label style={styles.label}>Vade Tarihi</label>
                                    {isEditing ? (
                                        <input
                                            type="date"
                                            style={styles.input}
                                            value={editedTransaction.dueDate || ''}
                                            onChange={(e) => setEditedTransaction(prev => ({ ...prev, dueDate: e.target.value }))}
                                        />
                                    ) : (
                                        <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                                            {selectedTransaction.dueDate ? new Date(selectedTransaction.dueDate).toLocaleDateString('tr-TR') : '-'}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div style={{ marginBottom: '16px' }}>
                                <label style={styles.label}>İlgili Taraf</label>
                                <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>{selectedTransaction.relatedPartyName}</div>
                            </div>
                            <div style={{ marginBottom: '16px' }}>
                                <label style={styles.label}>Tutar</label>
                                {isEditing ? (
                                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '8px' }}>
                                        <input
                                            type="number"
                                            style={styles.input}
                                            value={editedTransaction.amount}
                                            onChange={(e) => setEditedTransaction(prev => ({ ...prev, amount: e.target.value }))}
                                        />
                                        <select
                                            style={styles.select}
                                            value={editedTransaction.currency || 'TRY'}
                                            onChange={(e) => setEditedTransaction(prev => ({ ...prev, currency: e.target.value }))}
                                        >
                                            <option value="TRY">TRY (₺)</option>
                                            <option value="USD">USD ($)</option>
                                        </select>
                                    </div>
                                ) : (
                                    <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', fontWeight: '600' }}>
                                        {canViewPrices ? `${selectedTransaction.amount.toFixed(2)} ${selectedTransaction.currency === 'USD' ? '$' : '₺'}` : '***'}
                                        {selectedTransaction.currency === 'USD' && <span style={{ fontSize: '13px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>(Kur: {selectedTransaction.exchangeRate})</span>}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label style={styles.label}>Açıklama</label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        style={styles.input}
                                        value={editedTransaction.description}
                                        onChange={(e) => setEditedTransaction(prev => ({ ...prev, description: e.target.value }))}
                                    />
                                ) : (
                                    <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>{selectedTransaction.description}</div>
                                )}
                            </div>
                        </div>
                        <div style={styles.modalActions}>
                            {isEditing ? (
                                <>
                                    <button onClick={() => { setIsEditing(false); setEditedTransaction({ ...selectedTransaction }); }} style={{ ...styles.button, background: 'linear-gradient(135deg, #64748b, #475569)' }}>İptal</button>
                                    <button onClick={handleUpdateTransaction} style={{ ...styles.button, background: 'linear-gradient(135deg, #059669, #047857)' }}>Kaydet</button>
                                </>
                            ) : (
                                <>
                                    <button onClick={() => setIsEditing(true)} style={{ ...styles.button, background: 'linear-gradient(135deg, #3b82f6, #2563eb)' }}>Düzenle</button>
                                    <button onClick={() => handleDeleteTransaction(selectedTransaction)} style={{ ...styles.button, background: 'linear-gradient(135deg, #dc2626, #b91c1c)' }}>Sil</button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CashAndBank;
